/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  PROVIDER_COMPLETE: 'transition/provider-complete',
  LATE_COMPLETE: 'transition/complete-late',
  PROVIDER_FINAL_ACCEPT: 'transition/provider-final-accept',
  PROVIDER_FINAL_DECLINE: 'transition/provider-final-decline',
  PROVIDER_FINAL_COMPLETE: 'transition/provider-final-complete',
  CANCEL_AFTER_CONFIRMATION_BY_PROVIDER: 'transition/cancel-after-confirmation-by-provider',
  CANCEL_AFTER_CONFIRMATION_BY_CUSTOMER: 'transition/cancel-after-confirmation-by-customer',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // When the provider accepts or declines a transaction from the
  // SalePage, it is transitioned with the accept or decline transition.
  ACCEPT: 'transition/accept',
  DECLINE: 'transition/decline',

  // The operator can accept or decline the offer on behalf of the provider
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_DECLINE: 'transition/operator-decline',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',

  // Admin can also cancel the transition.
  CANCEL: 'transition/cancel',
  CANCEL_BY_CUSTOMER: 'transition/cancel-by-customer',

  // The backend will mark the transaction completed.
  COMPLETE: 'transition/complete',
  OPERATOR_COMPLETE: 'transition/operator-complete',
  INVOICE_CREATION: 'transition/invoice-generated',
  CANCEL_BY_OPERATOR: 'transition/cancel-by-operator',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  ACCEPTED: 'accepted',
  EXPIRED: 'expired',
  CANCELED: 'bidDeclined',
  DELIVERED: 'delivered',
  PROVIDER_FINAL_DECISTION: 'providerFinalDecision',
  BID_CONFIRMED: 'bidConfirmed',
  BID_ACCEPTED: 'bidAccepted',
  INVOICE_GENERATED: 'invoiceGenerated',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-bidpayment/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.CANCEL]: states.CANCELED,
        [transitions.CANCEL_BY_CUSTOMER]: states.CANCELED,
        [transitions.COMPLETE]: states.BID_ACCEPTED,
        [transitions.OPERATOR_COMPLETE]: states.BID_ACCEPTED,
        [transitions.PROVIDER_COMPLETE]: states.BID_ACCEPTED,
        [transitions.CANCEL_BY_OPERATOR]: states.CANCELED,
        [transitions.LATE_COMPLETE]: states.PROVIDER_FINAL_DECISTION,
      },
    },
    [states.PROVIDER_FINAL_DECISTION]: {
      on: {
        [transitions.PROVIDER_FINAL_ACCEPT]: states.BID_CONFIRMED,
        [transitions.PROVIDER_FINAL_DECLINE]: states.CANCELED,
      },
    },
    
    [states.BID_CONFIRMED]: {
      on: {
        [transitions.PROVIDER_FINAL_COMPLETE]: states.BID_CONFIRMED,
        [transitions.CANCEL_AFTER_CONFIRMATION_BY_PROVIDER]: states.CANCELED,
        [transitions.CANCEL_AFTER_CONFIRMATION_BY_CUSTOMER]: states.CANCELED,
      },
    },

    [states.EXPIRED]: {},

    [states.BID_ACCEPTED]: { 
      on: { [transitions.INVOICE_CREATION]: states.INVOICE_GENERATED } 
    },

    [states.CANCELED]: {},
    
    [states.INVOICE_GENERATED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.ACCEPT,
    transitions.OPERATOR_ACCEPT,
    transitions.CANCEL,
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.CONFIRM_PAYMENT,
    transitions.DECLINE,
    transitions.CANCEL_BY_CUSTOMER,
    transitions.OPERATOR_DECLINE,
    transitions.EXPIRE,
    transitions.INVOICE_CREATION,
  ].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [transitions.REQUEST_PAYMENT, transitions.REQUEST_PAYMENT_AFTER_INQUIRY].includes(
    transition
  );
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE,
    transitions.CANCEL,
    transitions.DECLINE,
  ];
  return txRefundedTransitions.includes(transition);
};

// INQUIRY is here because on this process a new bid sits in `inquiry` awaiting
// the RFP owner, who is the transaction *provider*. Without it, bidpayment
// contributed no transition to getTransitionsNeedingProviderAttention() and the
// new-bid badge only appeared to work via the legacy default-bid process
// happening to contribute a transition of the same name — the collision that
// transaction.js warns about.
export const statesNeedingProviderAttention = [states.INQUIRY, states.PREAUTHORIZED];